/**
 * Serviço de autenticação que encapsula o Firebase Auth.
 * Use a instância exportada (firebaseAuthService) e seus métodos em vez de funções soltas.
 */

import {
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  createUserWithEmailAndPassword as firebaseCreateUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  updatePassword as firebaseUpdatePassword,
  signInWithPhoneNumber as firebaseSignInWithPhoneNumber,
  linkWithPhoneNumber as firebaseLinkWithPhoneNumber,
  linkWithCredential as firebaseLinkWithCredential,
  signInWithPopup as firebaseSignInWithPopup,
  signInWithRedirect as firebaseSignInWithRedirect,
  GoogleAuthProvider,
  FacebookAuthProvider,
  PhoneAuthProvider,
  RecaptchaVerifier,
  type UserCredential,
  type User,
  type Auth,
  type ConfirmationResult,
  type ApplicationVerifier,
} from 'firebase/auth';
import { getFirebaseAuth } from './firebase-app';

export type { ConfirmationResult, ApplicationVerifier };
export { RecaptchaVerifier };

function wrapAuthError(error: unknown): Error {
  if (import.meta.env.DEV) {
    console.error('[Firebase Auth] Erro completo (desenvolvimento):', {
      error,
      code: (error as { code?: string })?.code,
      message: (error as Error)?.message,
    });
  }
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
}

export class FirebaseAuthService {
  /** Retorna a instância do Firebase Auth (para RecaptchaVerifier etc.). */
  getAuth(): Auth {
    return getFirebaseAuth();
  }

  /** Retorna o UID do usuário atualmente autenticado. */
  getCurrentUserUid(): string | null {
    return this.getAuth().currentUser?.uid ?? null;
  }

  /**
   * E-mail da conta Auth para persistir em perfil (onboarding, etc.).
   * Usa `user.email` ou `providerData[].email` — não depende de input de formulário.
   */
  resolveAccountEmail(user: User | null): string | null {
    if (!user) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- diagnóstico local
        console.debug('[RetornaAr:AuthEmail] resolveAccountEmail', { hasUser: false, result: null });
      }
      return null;
    }
    const direct = user.email?.trim();
    if (direct) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- diagnóstico local
        console.debug('[RetornaAr:AuthEmail] resolveAccountEmail', {
          uid: user.uid,
          source: 'user.email',
          providerIds: user.providerData.map((p) => p.providerId),
          result: direct,
        });
      }
      return direct;
    }
    for (const p of user.providerData) {
      const fromProvider = typeof p.email === 'string' ? p.email.trim() : '';
      if (fromProvider.length > 0) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console -- diagnóstico local
          console.debug('[RetornaAr:AuthEmail] resolveAccountEmail', {
            uid: user.uid,
            source: 'providerData',
            providerId: p.providerId,
            providerIds: user.providerData.map((x) => x.providerId),
            result: fromProvider,
          });
        }
        return fromProvider;
      }
    }
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- diagnóstico local
      console.debug('[RetornaAr:AuthEmail] resolveAccountEmail', {
        uid: user.uid,
        source: 'none',
        userEmailField: user.email ?? null,
        providerIds: user.providerData.map((p) => p.providerId),
        providersWithEmailFlag: user.providerData.map((p) => Boolean(p.email)),
        result: null,
      });
    }
    return null;
  }

  async signInWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<UserCredential> {
    try {
      return await firebaseSignInWithEmailAndPassword(
        this.getAuth(),
        email,
        password
      );
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  async createUserWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<UserCredential> {
    try {
      return await firebaseCreateUserWithEmailAndPassword(
        this.getAuth(),
        email,
        password
      );
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  async signOutUser(): Promise<void> {
    try {
      await firebaseSignOut(this.getAuth());
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  async sendPasswordResetEmailToUser(email: string): Promise<void> {
    try {
      await firebaseSendPasswordResetEmail(this.getAuth(), email);
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  async updateUserPassword(user: User, newPassword: string): Promise<void> {
    try {
      await firebaseUpdatePassword(user, newPassword);
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  async sendVerificationCodeToPhone(
    phoneNumber: string,
    appVerifier: ApplicationVerifier
  ): Promise<ConfirmationResult> {
    try {
      return await firebaseSignInWithPhoneNumber(
        this.getAuth(),
        phoneNumber,
        appVerifier
      );
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  /**
   * Envia SMS para vincular o telefone ao usuário já autenticado (ex.: login Google/Meta + onboarding).
   * Não usar para login só com número — use {@link sendVerificationCodeToPhone}.
   */
  async sendVerificationCodeToLinkPhone(
    user: User,
    phoneNumber: string,
    appVerifier: ApplicationVerifier
  ): Promise<ConfirmationResult> {
    try {
      return await firebaseLinkWithPhoneNumber(user, phoneNumber, appVerifier);
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  async resendVerificationCodeToPhone(
    phoneNumber: string,
    appVerifier: ApplicationVerifier
  ): Promise<ConfirmationResult> {
    try {
      return await firebaseSignInWithPhoneNumber(
        this.getAuth(),
        phoneNumber,
        appVerifier
      );
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  /** Reenvia SMS para vincular telefone (mesmo fluxo de {@link sendVerificationCodeToLinkPhone}). */
  async resendVerificationCodeToLinkPhone(
    user: User,
    phoneNumber: string,
    appVerifier: ApplicationVerifier
  ): Promise<ConfirmationResult> {
    return this.sendVerificationCodeToLinkPhone(user, phoneNumber, appVerifier);
  }

  async confirmPhoneVerificationCode(
    confirmationResult: ConfirmationResult,
    code: string
  ): Promise<UserCredential> {
    try {
      return await confirmationResult.confirm(code);
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  /**
   * Vincula (link) o telefone ao usuário autenticado (ex.: social login),
   * usando o `verificationId` obtido no envio do SMS + o código digitado.
   *
   * Importante: este fluxo NÃO faz sign-in com telefone (evita trocar o currentUser).
   */
  async linkPhoneNumberToCurrentUserWithCredential(params: {
    verificationId: string;
    code: string;
  }): Promise<UserCredential> {
    try {
      const user = this.getAuth().currentUser;
      if (!user) {
        throw new Error('Nenhum usuário autenticado para vincular o telefone.');
      }

      const credential = PhoneAuthProvider.credential(
        params.verificationId,
        params.code
      );

      return await firebaseLinkWithCredential(user, credential);
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  async signInWithGoogle(): Promise<UserCredential> {
    try {
      const provider = new GoogleAuthProvider();
      return await firebaseSignInWithPopup(this.getAuth(), provider);
    } catch (e) {
      throw wrapAuthError(e);
    }
  }

  /**
   * Facebook/Meta via Firebase Auth (popup). Facebook JS SDK no `index.html` não é
   * obrigatório — configure o provedor no Firebase + URIs OAuth no app Meta.
   */
  async signInWithMeta(): Promise<UserCredential> {
    try {
      const provider = new FacebookAuthProvider();
      return await firebaseSignInWithPopup(this.getAuth(), provider);
    } catch (e) {
      throw wrapAuthError(e);
    }
  }
}

/** Instância única do serviço de autenticação. Use esta instância em todo o app. */
export const firebaseAuthService = new FirebaseAuthService();
